import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import ReportCycleUpsellSvg from 'ext-ui/svg/illustration/ReportCycleUpsellSvg'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Accordion from 'src/components/Accordion/Accordion'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import ButtonAndMoreDropdown from 'src/components/ButtonAndMoreDropdown/ButtonAndMoreDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ListBox, { TitledCell } from 'src/components/ListBox/ListBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { MoreDropdownDesigns } from 'src/components/MoreDropdown/MoreDropdown'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import SvgClockRewind from 'src/components/Svgs/SvgClockRewind'
import SvgPreviousArrow from 'src/components/Svgs/SvgPreviousArrow'
import SvgSettings from 'src/components/Svgs/SvgSettings'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import SaasUpgradeOrContent from 'src/feature/SaasUpgradeOrContent/SaasUpgradeOrContent'
import { useTranslate } from 'src/localization/Trns'
import {
  OrgExpenseReportCycle,
  OrgExpenseReportCycleModel,
} from 'src/models/orgExpenseReports.models'
import { UserFeature } from 'src/models/user.models'
import Lnk from 'src/navigation/Lnk'
import {
  orgExpenseReportsHistoricalReportsUrl,
  orgExpenseReportUrl,
  setOrgExpenseReportCycleUrl,
} from 'src/navigation/urls'
import {
  issuerGroupedRCNOrgExpenseReportCycles,
  useQueryOrgExpenseReportCycles,
} from 'src/queries/orgExpenseReports.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectAppName,
  selectBrandColorCta,
  selectBrandColorFontPrimary,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const ExpensesExpenseReports: React.FC = () => {
  const translate = useTranslate()
  const user = useSelector(selectUserListModelFromState)
  const appName = useSelector(selectAppName)
  const brandColorFontPrimary = useSelector(selectBrandColorFontPrimary)
  const brandColorCta = useSelector(selectBrandColorCta)
  const { data: reports, isLoading: loading } = useQueryOrgExpenseReportCycles({
    select: issuerGroupedRCNOrgExpenseReportCycles,
  })

  const { analytics } = useAnalyticsContext()

  const sortRcns = (
    a: OrgExpenseReportCycle,
    b: OrgExpenseReportCycle,
  ): number => {
    return a.accountName > b.accountName
      ? 1
      : b.accountName > a.accountName
        ? -1
        : 0
  }

  return (
    <>
      <Helmet>
        <title>{`Statements | ${appName}`}</title>
        <meta name="description" content={`Statements page of ${appName}`} />
      </Helmet>
      <SaasUpgradeOrContent
        illustration={<ReportCycleUpsellSvg />}
        title={translate('Expense Report Cycle')}
        description={translate(
          "For each billing cycle, you'll receive an automatically generated expense report matching your bank statement—complete with all virtual* and physical card transactions (if applicable).",
        )}
        disclaimer={translate(
          '*Virtual cards available for ${selectIssuersLink}',
          {
            selectIssuersLink: (
              <Lnk
                className="underline-link"
                href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                ariaLabel={translate('select issuers')}
              >
                {translate('select issuers')}
              </Lnk>
            ),
          },
        )}
        ctaDetailSrc={
          'Upgrade to ${tier} today and receive automatically generated expense reports synced with your billing cycle.'
        }
        feature={UserFeature.AUTOMATED_EXPENSE_REPORTS}
      >
        <BackgroundPageContent noStackingIndex noPadding>
          {user?.hasOrgBookkeeperPermissions ? (
            loading ? (
              <LoadingBackgroundPage />
            ) : reports && reports.length <= 0 ? (
              <TableEmptyState
                design={TableEmptyStateDesigns.OrgExpenseReportCycleSettings}
              />
            ) : (
              <>
                {reports &&
                  reports.map((issuer) => {
                    return (
                      <Accordion
                        key={issuer.issuerName}
                        topContent={
                          <div
                            css={css`
                              display: flex;
                              flex-direction: row;
                              align-items: center;
                              justify-content: space-between;
                              width: 100%;
                            `}
                          >
                            <TitledCell
                              label={translate('Bank')}
                              content={
                                <span
                                  css={css`
                                    ${getFontCss(
                                      20,
                                      themeConstants.colors.font_primary,
                                      styleConstants.fontFamilies.medium,
                                    )};
                                  `}
                                >
                                  {issuer.issuerName}
                                </span>
                              }
                              isTitle
                            />
                          </div>
                        }
                        collapsibleContent={
                          <>
                            {issuer.rcns
                              .sort(sortRcns)
                              .map((rcn: OrgExpenseReportCycleModel) => {
                                return (
                                  <ListBox
                                    key={rcn.id}
                                    id={'rcn-' + rcn.id}
                                    linkProps={{
                                      to:
                                        rcn.currentReportId !== undefined
                                          ? orgExpenseReportsHistoricalReportsUrl(
                                              rcn.id,
                                            )
                                          : setOrgExpenseReportCycleUrl(rcn.id),
                                      ariaLabelSource: 'Account Expense Report',
                                    }}
                                    cells={[
                                      {
                                        isTitle: true,
                                        label: translate('Account Name'),
                                        content: (
                                          <div
                                            css={css`
                                              display: flex;
                                              flex-direction: row;
                                              gap: 8px;
                                            `}
                                          >
                                            <span
                                              id="title"
                                              css={css`
                                                ${getFontCss(
                                                  16,
                                                  themeConstants.colors
                                                    .font_primary,
                                                  styleConstants.fontFamilies
                                                    .medium,
                                                )};
                                              `}
                                            >
                                              <span
                                                css={css`
                                                  padding-inline-end: 8px;
                                                `}
                                              >
                                                {rcn.accountName}
                                              </span>
                                              <Tag
                                                text={rcn.accountLast4?.toString()}
                                                size={TagSizes.Medium}
                                              />
                                            </span>
                                            <SvgChevronRight
                                              id={'chevron'}
                                              css={css`
                                                transition: transform
                                                  ${styleConstants
                                                    .transitionSpeed.fast}
                                                  ease-out;
                                                transform: scale(calc(20 / 24));

                                                ${getBreakpointDown(
                                                  breakpointSizes.SM,
                                                )(css`
                                                  display: none;
                                                `)}
                                              `}
                                            />
                                          </div>
                                        ),
                                      },
                                      {
                                        label: translate('Account Holder'),
                                        content: (
                                          <>
                                            <UserAvatarAndName
                                              textCss={css`
                                                ${getFontCss(
                                                  16,
                                                  themeConstants.colors
                                                    .font_primary,
                                                  styleConstants.fontFamilies
                                                    .regular,
                                                )};
                                              `}
                                              user={{
                                                id: rcn.accountUserId || '',
                                                fullNameOrEmail:
                                                  rcn.accountUserFullName ||
                                                  rcn.accountUserEmail ||
                                                  '',
                                                initials:
                                                  `${rcn.accountUserFirstName?.slice(0, 1)}${rcn.accountUserLastName?.slice(0, 1)}` ||
                                                  rcn.accountUserEmail[0] ||
                                                  '',
                                                avatarUrl:
                                                  rcn.accountUserAvatarUrl,
                                                hasFullName: true,
                                              }}
                                            />
                                          </>
                                        ),
                                      },
                                      {
                                        label: translate('Statement End Date'),
                                        content:
                                          rcn.currentReportEndDate !==
                                          undefined ? (
                                            <>{rcn.cycleLabel}</>
                                          ) : (
                                            <HoverableLink
                                              to={setOrgExpenseReportCycleUrl(
                                                rcn.id,
                                              )}
                                              ariaLabel={'Add Account'}
                                              size={HoverableLinkSizes.Large}
                                              underline
                                            >
                                              {translate('Set up statement')}
                                            </HoverableLink>
                                          ),
                                      },
                                    ]}
                                    icon={
                                      rcn.currentReportId !== undefined ? (
                                        <ButtonAndMoreDropdown
                                          buttonProps={{
                                            design: ButtonDesigns.plainNoShadow,
                                            type: ButtonTypes.button,
                                            text: translate(
                                              'View Current Statement',
                                            ),
                                            id: 'viewCurrentReport',
                                            testId: 'viewCurrentReport',
                                            linkProps: {
                                              to: orgExpenseReportUrl(
                                                rcn.id,
                                                rcn.currentReportId,
                                              ),
                                              ariaLabel:
                                                'View Current Statement',
                                            },
                                            onClick: (): void => {
                                              analytics?.track(
                                                'expense reports view report',
                                                {
                                                  report_id:
                                                    rcn.currentReportId,
                                                },
                                              )
                                            },
                                          }}
                                          moreDropdownProps={{
                                            id: 'moreDropdown-' + rcn.id,
                                            design:
                                              MoreDropdownDesigns.WhiteToGray,
                                            minWidth: 270,
                                            verticalBuffer: 10,
                                            topOptions: [
                                              {
                                                id: 'viewPreviousReport',
                                                testId: 'viewPreviousReport',
                                                key: 'viewPreviousReport',
                                                label: (
                                                  <div
                                                    css={css`
                                                      display: flex;
                                                      flex-direction: row;
                                                      align-items: center;
                                                      gap: 8px;
                                                      left: -2px;
                                                      position: relative;
                                                      ${getFontCss(
                                                        14,
                                                        themeConstants.colors
                                                          .cta,
                                                        styleConstants
                                                          .fontFamilies.regular,
                                                      )};
                                                    `}
                                                  >
                                                    <SvgPreviousArrow />
                                                    {translate(
                                                      'View Previous Statement',
                                                    )}
                                                  </div>
                                                ),
                                                grayed:
                                                  rcn.previousReportId ===
                                                  undefined,
                                                to:
                                                  rcn.previousReportId &&
                                                  orgExpenseReportUrl(
                                                    rcn.id,
                                                    rcn.previousReportId,
                                                  ),
                                                ariaLabelSource:
                                                  'View Previous Statement',
                                                onClick: (): void => {
                                                  analytics?.track(
                                                    'expense reports overflow menu',
                                                    {
                                                      menu_selection:
                                                        'previous report',
                                                    },
                                                  )
                                                },
                                              },
                                              {
                                                id: 'historicalReports',
                                                testId: 'historicalReports',
                                                key: 'historicalReports',
                                                label: (
                                                  <div
                                                    css={css`
                                                      display: flex;
                                                      flex-direction: row;
                                                      align-items: center;
                                                      gap: 8px;
                                                      ${getFontCss(
                                                        14,
                                                        themeConstants.colors
                                                          .cta,
                                                        styleConstants
                                                          .fontFamilies.regular,
                                                      )};
                                                    `}
                                                  >
                                                    <SvgClockRewind />
                                                    {translate(
                                                      'View All Statements',
                                                    )}
                                                  </div>
                                                ),
                                                to: orgExpenseReportsHistoricalReportsUrl(
                                                  rcn.id,
                                                ),
                                                ariaLabelSource:
                                                  'View All Statements',
                                                onClick: (): void => {
                                                  analytics?.track(
                                                    'expense reports overflow menu',
                                                    {
                                                      menu_selection:
                                                        'all reports',
                                                    },
                                                  )
                                                },
                                              },
                                            ],
                                            bottomOptions: [
                                              {
                                                id: 'cycleSettings',
                                                testId: 'cycleSettings',
                                                key: 'cycleSettings',
                                                label: (
                                                  <div
                                                    css={css`
                                                      display: flex;
                                                      flex-direction: row;
                                                      align-items: center;
                                                      gap: 8px;
                                                      left: -6px;
                                                      position: relative;
                                                      ${getFontCss(
                                                        14,
                                                        themeConstants.colors
                                                          .cta,
                                                        styleConstants
                                                          .fontFamilies.regular,
                                                      )};
                                                    `}
                                                  >
                                                    <SvgSettings />
                                                    {translate(
                                                      'Go to Statement Cycle Settings',
                                                    )}
                                                  </div>
                                                ),
                                                to: setOrgExpenseReportCycleUrl(
                                                  rcn.id,
                                                ),
                                                ariaLabelSource:
                                                  'Go to Statement Cycle Settings',
                                                onClick: (): void => {
                                                  analytics?.track(
                                                    'expense reports overflow menu',
                                                    {
                                                      menu_selection:
                                                        'report cycle',
                                                    },
                                                  )
                                                },
                                              },
                                            ],
                                            hover: true,
                                            bottomOptionsCtaStyle: true,
                                            openComponent: (
                                              <SvgChevronUp
                                                css={css`
                                                  transform: scale(0.667);
                                                `}
                                                fill={brandColorCta}
                                              />
                                            ),
                                            closedComponent: (
                                              <SvgChevronDown
                                                css={css`
                                                  transform: scale(0.667);
                                                `}
                                                fill={brandColorFontPrimary}
                                              />
                                            ),
                                          }}
                                        />
                                      ) : (
                                        <div
                                          css={css`
                                            width: 220px;
                                          `}
                                        ></div>
                                      )
                                    }
                                  />
                                )
                              })}
                          </>
                        }
                      />
                    )
                  })}
              </>
            )
          ) : (
            <NotFoundBackgroundPageContent />
          )}
        </BackgroundPageContent>
      </SaasUpgradeOrContent>
    </>
  )
}

export default ExpensesExpenseReports
